import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { portalProofPreviewUrl, portalApproveProof, portalRequestProofChanges, portalUploadDesignFile, extractApiError } from "@/lib/api";

export const SUPPLY_STEPS = [
  ["paid_confirmed", "Paid / Confirmed"], ["proof_sent", "Proof Sent"], ["proof_approved", "Final Proof Approved"],
  ["in_production", "In Production"], ["ready", "Ready for Pickup / Delivery"], ["delivered", "Delivered / Completed"],
];
export const SUPPLY_LABELS = { ...Object.fromEntries(SUPPLY_STEPS), changes_requested: "Changes Requested", out_for_delivery: "Out for Delivery", shipped: "Shipped" };
const READY_LABEL = { pickup: "Ready for Pickup", delivery: "Out for Delivery", shipping: "Shipped", event: "Ready for Event Delivery & Setup" };
const PROOF_STATUS = { pending: "Awaiting your review", approved: "Final Proof Approved", changes_requested: "Changes requested", superseded: "Superseded" };

export const proofStatusText = (p) => {
  const when = (t) => (t ? ` · ${new Date(t).toLocaleString()}` : "");
  if (p.status === "superseded") {
    return `${p.decision === "changes_requested" ? `Changes requested${when(p.responded_at)} → ` : ""}Superseded by v${p.superseded_by_version}`;
  }
  if (p.status === "approved") return `Final Proof Approved${when(p.approved_at)}`;
  if (p.status === "changes_requested") return `Changes requested${when(p.responded_at)}`;
  return PROOF_STATUS[p.status] || p.status;
};

const ProofImages = ({ token, proof }) => (
  <div className="mt-3 space-y-3 select-none" onContextMenu={(e) => e.preventDefault()} data-testid={`proof-images-v${proof.version}`}>
    {Array.from({ length: proof.page_count || 0 }).map((_, i) => (
      <img key={i} src={portalProofPreviewUrl(token, proof.id, i)} alt={`Proof v${proof.version} page ${i + 1}`}
           draggable={false} className="max-h-[32rem] w-auto max-w-full border pointer-events-none" style={{ WebkitUserDrag: "none" }} />
    ))}
  </div>
);

const ProofReview = ({ token, proof, onDone }) => {
  const [confirm, setConfirm] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (fn, payload, msg) => {
    setBusy(true);
    try { await fn(token, proof.id, payload); toast.success(msg); onDone(); } catch (e) { toast.error(extractApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-4 space-y-4" data-testid="proof-review">
      <label className="flex gap-3 items-start text-sm cursor-pointer">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1" data-testid="proof-confirm-checkbox" />
        <span>I have reviewed all names, spelling, dates, quantities, colors, personalization, placement and other visible details on proof version {proof.version}, and I approve it as my final proof. I authorize Simply Social to produce my items according to this approved proof.</span>
      </label>
      <button className="btn-primary" disabled={!confirm || busy} data-testid="proof-approve-btn"
              onClick={() => act(portalApproveProof, { confirm: true }, "Final proof approved")}>Approve Proof v{proof.version}</button>
      <div className="pt-2">
        <textarea className="editorial-input text-sm" rows={2} placeholder="Describe the changes you need" value={comment}
                  onChange={(e) => setComment(e.target.value)} data-testid="proof-changes-comment" />
        <button className="btn-outline mt-2" disabled={busy || comment.trim().length < 3} data-testid="proof-request-changes-btn"
                onClick={() => act(portalRequestProofChanges, { comment }, "Change request sent")}>Request Changes</button>
      </div>
    </div>
  );
};

export const DesignFiles = ({ token, data, onDone }) => {
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    setBusy(true);
    for (const f of files) {
      try { await portalUploadDesignFile(token, f); } catch (e) { toast.error(extractApiError(e, `Could not upload ${f.name}`)); }
    }
    setBusy(false);
    onDone();
  };
  return (
    <div className="text-sm" data-testid="portal-design-files">
      {!data.can_upload_design_files && !(data.design_files || []).length ? null : <div className="font-label text-[0.55rem] mb-1" style={{ color: "var(--ss-muted)" }}>Your photos, logos & inspiration</div>}
      {(data.design_files || []).map((f) => <div key={f.id} className="text-xs">{f.filename}</div>)}
      {data.can_upload_design_files && (
        <input type="file" multiple accept="image/*,application/pdf" disabled={busy} className="mt-2 text-sm" data-testid="portal-design-files-input"
               onChange={(e) => add(Array.from(e.target.files || []))} />
      )}
    </div>
  );
};

export const SupplyPortalPanel = ({ token, data, onDone, onPayRevision, paying }) => {
  const status = data.supply_status;
  const fulfil = data.quote?.fulfillment;
  const steps = SUPPLY_STEPS.map(([k, l]) => [k, k === "ready" && READY_LABEL[fulfil] ? READY_LABEL[fulfil] : l]);
  const idx = steps.findIndex((s) => s[0] === ({ changes_requested: "proof_sent", out_for_delivery: "ready", shipped: "ready" }[status] || status));
  const fee = data.revision_fee_due;
  return (
    <div className="p-6 border space-y-5" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-supply-panel">
      <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-rust)" }}>Social Supply Custom Order</div>
      <ol className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="supply-steps">
        {steps.map(([k, label], i) => (
          <li key={k} className="flex items-center gap-2 text-xs" style={{ color: i <= idx ? "var(--ss-cream)" : "var(--ss-muted)" }}>
            <span className="h-6 w-6 shrink-0 flex items-center justify-center border" style={{ borderColor: i <= idx ? "var(--ss-rust)" : "var(--ss-border)", background: i <= idx ? "var(--ss-rust)" : "transparent", color: "#F5F1E8" }}>
              {i <= idx ? <Check size={12} /> : i + 1}
            </span>
            {label}
          </li>
        ))}
      </ol>
      {status === "changes_requested" && !fee && <p className="text-sm" data-testid="supply-changes-note">We received your change request and will send a revised proof.</p>}
      <p className="text-sm font-semibold" data-testid="portal-proof-reminder">Production begins only after you approve your final design proof.</p>
      <p className="text-xs" style={{ color: "var(--ss-muted)" }} data-testid="portal-revision-rounds">Design revisions: {Math.min(data.revision_rounds_used, data.revisions_included)} of {data.revisions_included} included rounds used{data.revision_rounds_used > data.revisions_included ? ` · ${data.revision_rounds_used - data.revisions_included} additional round(s)` : ""}. Additional rounds are $15 each. Once you approve your final proof the design is final; later changes may require an additional fee or a new order and may affect the completion date.</p>
      {fee && (
        <div className="p-4 border" style={{ borderColor: "var(--ss-rust)" }} data-testid="portal-revision-fee">
          <p className="text-sm mb-3">This change request is revision round {fee.round}. Your order includes {data.revisions_included} rounds, so this round is ${Number(fee.amount).toFixed(2)}. We'll prepare your next proof once it is paid.</p>
          <button className="btn-primary" disabled={paying} onClick={onPayRevision} data-testid="portal-pay-revision">{paying ? "Opening Stripe…" : `Pay Revision Fee · $${Number(fee.amount).toFixed(2)}`}</button>
        </div>
      )}
      {data.tracking && (
        <div className="text-sm" data-testid="portal-tracking">Tracking: {data.tracking.carrier} {data.tracking.tracking_number}
          {data.tracking.tracking_url && /^https?:\/\//.test(data.tracking.tracking_url) && <> · <a className="underline" href={data.tracking.tracking_url} target="_blank" rel="noreferrer">Track package</a></>}
        </div>
      )}
      {(data.can_upload_design_files || (data.design_files || []).length > 0) && <DesignFiles token={token} data={data} onDone={onDone} />}
      {status === "proof_approved" && <p className="text-sm" data-testid="supply-approved-note">Final proof approved. We'll notify you when production begins.</p>}
      {(data.proofs || []).slice().reverse().map((p) => (
        <div key={p.id} className="border p-4" style={{ borderColor: "var(--ss-border)" }} data-testid={`proof-v${p.version}`}>
          <div className="flex flex-wrap justify-between gap-2 text-sm">
            <strong>Proof Version {p.version}</strong>
            <span style={{ color: "var(--ss-muted)" }} data-testid={`proof-status-v${p.version}`}>{proofStatusText(p)}</span>
          </div>
          {p.note && <p className="text-sm mt-2" style={{ color: "var(--ss-cream-dim)" }}>{p.note}</p>}
          {p.status !== "superseded" && <ProofImages token={token} proof={p} />}
          {p.client_comment && <p className="text-xs mt-2" style={{ color: "var(--ss-muted)" }}>Your note: {p.client_comment}</p>}
          {p.status === "pending" && status === "proof_sent" && <ProofReview token={token} proof={p} onDone={onDone} />}
        </div>
      ))}
      {!(data.proofs || []).length && status === "paid_confirmed" && <p className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>Your design proof will appear here for review.</p>}
    </div>
  );
};
